import { useCallback, useState } from 'react'

import { useLevelPlaySurface } from '@/shared/level-play'
import { FormControl } from '@/shared/ui/Form'
import { QrCodeReader } from '@/shared/ui/QrCodeReader'

import { activity as activityConfig } from './config'

// O decodificador entrega o mesmo código dezenas de vezes por segundo enquanto
// o QR segue enquadrado; o cooldown evita gastar uma requisição por frame.
const COOLDOWN_MS = 700

export const LevelPlayQuestionQRCode = ({ activity }) => {
  const { onAnswer, disableInputs, answerIsRight, answerIsWrong } =
    useLevelPlaySurface()
  const { handleAnswerCallback } = activityConfig

  const [feedback, setFeedback] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [lastCode, setLastCode] = useState<string | null>(null)

  const borderColor = answerIsRight
    ? 'border-success'
    : answerIsWrong
    ? 'border-danger'
    : 'border-gray-200'

  const handleCode = useCallback(
    async (code: string) => {
      setLastCode(code)
      setFeedback(null)
      setProcessing(true)

      try {
        const payload = handleAnswerCallback({ answer: code, activity })
        await onAnswer({ uid: activity.uid, ...payload })

        // Mesma regra do `onAnswer` do nível: `skipValidation` (save_answer)
        // aceita sem validar; fora isso vale o resultado da comparação.
        const accepted = Boolean(
          payload.answerIsCorrect || payload.skipValidation
        )
        if (!accepted) setFeedback('Código inválido.')

        // Devolver `false` libera o código para ser lido de novo.
        return accepted
      } catch (error: any) {
        setFeedback(error?.message || String(error))
        return false
      } finally {
        setProcessing(false)
      }
    },
    [activity, handleAnswerCallback, onAnswer]
  )

  return (
    <div className="mx-auto flex flex-col h-full items-center max-w-[300px] md:max-w-[350px] lg:max-w-[400px]">
      <QrCodeReader
        mode="continuous"
        cooldownMs={COOLDOWN_MS}
        disabled={disableInputs}
        onSuccess={handleCode}
        className={`overflow-hidden mb-4 aspect-square border-[3px] ${borderColor} rounded-xl bg-gray-200`}
        overlay={
          processing ? (
            <div className="top-0 bg-black/25 absolute left-0 w-full h-full flex flex-col items-center justify-center">
              <div className="spinner-border" role="status" />
              <small className="mt-2 text-white font-semibold">
                Validando{lastCode ? `: ${lastCode}` : '...'}
              </small>
            </div>
          ) : null
        }
        fallback={
          <>
            <span className="mb-2 text-center text-gray-700 block font-bold">
              Ou digite o código
            </span>
            <FormControl
              name="answer"
              required
              placeholder="Insira aqui o código"
              disabled={disableInputs || processing}
              className="w-full"
            />
            {!!feedback && (
              <small className="mt-2 text-center text-danger">{feedback}</small>
            )}
          </>
        }
      />
    </div>
  )
}
